import { useCallback } from 'react';

import { ClientModel, ClientQueryResponse, SubEntityModel } from '@cko/dashboard-shared';
import { useMakeRequest } from './useMakeRequest';

export const useClientProviderFetcher = (basePath: string) => {
  const makeRequest = useMakeRequest(basePath);

  const fetchGetClient = useCallback(
    async (clientId: string): Promise<ClientModel | undefined> => {
      const response = await makeRequest({ clientId });
      if (response && 'client_id' in response) {
        return ClientModel.from(response);
      }
      return undefined;
    },
    [makeRequest],
  );

  const fetchGetInitialClient = useCallback(async (): Promise<
    ClientModel | SubEntityModel | undefined
  > => {
    const response = await makeRequest({ version: '2', limit: 1 });
    if (response && 'data' in response) {
      if (response.type === 'sub-entity') {
        return SubEntityModel.from(response.data);
      }
      if (response.data.length > 0) {
        return ClientModel.from(response.data[0]);
      }
    }

    return undefined;
  }, [makeRequest]);

  const fetchQueryClients = useCallback(
    async (
      query: string,
      limit?: number,
      skip?: number,
    ): Promise<ClientQueryResponse | undefined> => {
      const response = await makeRequest({ version: '2', query, limit, skip });
      if (response && 'data' in response && response.type === 'client') {
        return {
          ...response,
          data: response.data.map(ClientModel.from),
        };
      }

      return undefined;
    },
    [makeRequest],
  );

  return {
    fetchGetClient,
    fetchGetInitialClient,
    fetchQueryClients,
  };
};
